import {
  buildGamificationFlowState,
  clearStoredGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";

const SOCKET_URL = "wss://api.vidyakasam.in/web-socket";
const HANDSHAKE_TIMEOUT_MS = 3000;
const CONNECT_TIMEOUT_MS = 4000;
const RECONNECT_DELAY_MS = 1000;
const IDLE_DISCONNECT_MS = 1000;

function parseJsonSafely(value) {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function normalizeQuestionPayload(payload, selectedLang) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  return {
    ...payload,
    question_id: Number(payload.question_id || 0),
    next_question_id: Number(payload.next_question_id || 0),
    question_index: Number(payload.question_index || 0),
    total_questions: Number(payload.total_questions || 0),
    question_time: Number(payload.question_time || payload.remaining_time || 0),
    remaining_time: Number(payload.remaining_time || payload.question_time || 0),
    remaining_coins: Number(payload.remaining_coins || 0),
    selected_lang: selectedLang || "EN",
    question_option: Array.isArray(payload.question_option) ? payload.question_option : [],
    mm_options: Array.isArray(payload.mm_options) ? payload.mm_options : [],
    next_question_available_langs: Array.isArray(payload.next_question_available_langs)
      ? payload.next_question_available_langs
      : [],
  };
}

export class GamificationBattleClient {
  constructor() {
    this.socket = null;
    this.listeners = new Set();
    const storedState = getStoredGamificationFlowState();
    this.state = buildGamificationFlowState({
      ...storedState,
      room_request_pending: false,
      room_request_action: "",
      socketError: storedState?.room_request_pending
        ? "Previous room request was not confirmed. Check the room before trying again."
        : storedState?.socketError || "",
      isSocketConnected: false,
      connectionStatus: "disconnected",
    });
    this.userId = 0;
    this.reconnectTimer = null;
    this.handshakeTimer = null;
    this.connectTimer = null;
    this.idleDisconnectTimer = null;
    this.connectPromise = null;
    this.resolveConnection = null;
    this.shouldReconnect = false;
    this.hasRejoinAttempted = false;
    this.pendingLeaveIntent = "";
    this.roomRequestTimer = null;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);

    return () => {
      this.listeners.delete(listener);
    };
  }

  hasSubscribers() {
    return this.listeners.size > 0;
  }

  emit() {
    persistGamificationFlowState(this.state);
    this.listeners.forEach((listener) => listener(this.state));
  }

  patch(nextState) {
    this.state = buildGamificationFlowState(nextState, this.state);
    this.emit();
  }

  replace(nextState) {
    this.state = buildGamificationFlowState(nextState);
    this.emit();
  }

  isOffline() {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  }

  clearConnectionTimers() {
    if (this.handshakeTimer) window.clearTimeout(this.handshakeTimer);
    if (this.connectTimer) window.clearTimeout(this.connectTimer);
    this.handshakeTimer = null;
    this.connectTimer = null;
  }

  finishConnect(connected) {
    this.clearConnectionTimers();
    if (this.resolveConnection) this.resolveConnection(connected);
    this.resolveConnection = null;
    this.connectPromise = null;
  }

  scheduleIdleDisconnect() {
    if (this.idleDisconnectTimer) window.clearTimeout(this.idleDisconnectTimer);
    this.idleDisconnectTimer = window.setTimeout(() => {
      this.idleDisconnectTimer = null;
      if (!this.hasSubscribers()) this.disconnect(false);
    }, IDLE_DISCONNECT_MS);
  }

  handleOnline = () => {
    if (this.shouldReconnect && !this.socket && !this.reconnectTimer) {
      this.scheduleReconnect();
    }
  };

  handleOffline = () => {
    if (this.socket) this.failConnection(this.socket, "No internet connection.");
  };

  connect(userId) {
    const normalizedUserId = Number(userId || 0);
    if (!normalizedUserId) return Promise.resolve(false);

    if (this.idleDisconnectTimer) {
      window.clearTimeout(this.idleDisconnectTimer);
      this.idleDisconnectTimer = null;
    }

    if (this.socket && this.userId === normalizedUserId) {
      if (this.state.isSocketConnected && this.socket.readyState === WebSocket.OPEN) {
        return Promise.resolve(true);
      }
      if (this.connectPromise && [WebSocket.OPEN, WebSocket.CONNECTING].includes(this.socket.readyState)) {
        return this.connectPromise;
      }
    }

    this.disconnect(false);
    this.userId = normalizedUserId;
    this.shouldReconnect = true;
    this.hasRejoinAttempted = false;
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    if (this.isOffline()) {
      this.patch({
        connectionStatus: "disconnected",
        isSocketConnected: false,
        socketError: "No internet connection.",
      });
      this.scheduleReconnect();
      return Promise.resolve(false);
    }

    this.patch({
      connectionStatus: "connecting",
      isSocketConnected: false,
      socketError: "",
    });

    this.connectPromise = new Promise((resolve) => {
      this.resolveConnection = resolve;
    });

    let nextSocket;
    try {
      nextSocket = new WebSocket(`${SOCKET_URL}?user_id=${normalizedUserId}`);
    } catch (_error) {
      this.finishConnect(false);
      this.patch({
        connectionStatus: "disconnected",
        isSocketConnected: false,
        socketError: "Unable to connect. Reconnecting...",
      });
      this.scheduleReconnect();
      return Promise.resolve(false);
    }

    this.socket = nextSocket;
    this.handshakeTimer = window.setTimeout(() => {
      this.failConnection(nextSocket, "Connection timed out. Reconnecting...");
    }, HANDSHAKE_TIMEOUT_MS);
    this.connectTimer = window.setTimeout(() => {
      this.failConnection(nextSocket, "Connection timed out. Reconnecting...");
    }, CONNECT_TIMEOUT_MS);

    nextSocket.onopen = () => {
      if (this.socket !== nextSocket) return;
      this.patch({ connectionStatus: "connecting", isSocketConnected: false });
    };

    nextSocket.onmessage = (event) => {
      if (this.socket !== nextSocket) return;
      const message = parseJsonSafely(event.data);
      if (!message) return;

      if (message.socket_connected === true) {
        this.finishConnect(true);
        this.patch({
          connectionStatus: "connected",
          isSocketConnected: true,
          socketError: "",
          lastSocketAction: "socket_connected",
        });
        this.tryRejoin();
        return;
      }

      if (message.action === "ping") {
        this.sendAction("pong", {});
        return;
      }

      this.handleActionMessage(message);
    };

    nextSocket.onerror = () => {
      this.failConnection(nextSocket, "Socket connection error. Reconnecting...");
    };

    nextSocket.onclose = () => {
      this.failConnection(nextSocket, "Connection closed. Reconnecting...");
    };

    return this.connectPromise;
  }

  failConnection(socket, message) {
    if (this.socket !== socket) return;
    this.finishConnect(false);
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    this.socket = null;
    if (socket.readyState !== WebSocket.CLOSED) socket.close();
    this.patch({
      connectionStatus: "disconnected",
      isSocketConnected: false,
      socketError: message,
    });
    this.scheduleReconnect();
  }

  disconnect(clearSession = false) {
    this.shouldReconnect = false;
    this.pendingLeaveIntent = "";
    this.clearRoomRequestTimer();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    if (this.idleDisconnectTimer) window.clearTimeout(this.idleDisconnectTimer);
    this.reconnectTimer = null;
    this.idleDisconnectTimer = null;
    this.finishConnect(false);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);

    if (this.socket) {
      const socket = this.socket;
      this.socket = null;
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState !== WebSocket.CLOSED) socket.close();
    }

    this.patch({ connectionStatus: "disconnected", isSocketConnected: false });
    if (clearSession) this.clearSession();
  }

  scheduleReconnect() {
    if (!this.shouldReconnect || !this.userId || this.reconnectTimer || this.socket) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldReconnect) this.connect(this.userId);
    }, RECONNECT_DELAY_MS);
  }

  tryRejoin() {
    if (this.hasRejoinAttempted) {
      return;
    }

    const battleRoomId = Number(this.state?.battle_room_id || 0);
    if (!battleRoomId) {
      return;
    }

    const liveStage = this.state?.liveStage || "";
    if (!["starting", "gameplay", "waiting_for_answer"].includes(liveStage)) {
      return;
    }

    this.hasRejoinAttempted = true;
    this.sendAction("rejoinGame", {
      battle_room_id: battleRoomId,
    });
  }

  clearRoomRequestTimer() {
    if (this.roomRequestTimer) window.clearTimeout(this.roomRequestTimer);
    this.roomRequestTimer = null;
  }

  startRoomRequestTimer() {
    this.clearRoomRequestTimer();
    this.roomRequestTimer = window.setTimeout(() => {
      this.roomRequestTimer = null;
      if (this.state?.room_request_pending) this.patch({
        room_request_pending: false,
        room_request_action: "",
        socketError: "Room request could not be confirmed. Check your room before trying again.",
      });
    }, 12000);
  }

  handleActionMessage(message) {
    const action = message?.action || "";
    const payload = message?.data && typeof message.data === "object" ? message.data : {};

    if (action === "leaveGameError") {
      this.clearLiveIdentifiers();
      this.patch({ liveStage: "left", lastSocketAction: action });
      return;
    }

    if (action === "validationError") {
      this.clearRoomRequestTimer();
      this.patch({
        room_request_pending: false,
        room_request_action: "",
        lastSocketAction: action,
        validationError: payload,
        socketError: message?.message || "Something went wrong.",
      });
      return;
    }

    if (action === "notInQueue") {
      this.patch({
        lastSocketAction: action,
        socketError: message?.message || "You are no longer in the opponent queue.",
        start_confirmation_required: Boolean(this.state?.start_confirmed && this.state?.start_confirmation_required),
        start_countdown_seconds: this.state?.start_confirmed ? this.state?.start_countdown_seconds : 0,
      });
      return;
    }

    if (message?.status === "error" || (["createRoom", "joinRoom"].includes(action) && message?.status !== "success")) {
      if (["createRoom", "joinRoom"].includes(action)) this.clearRoomRequestTimer();
      this.patch({
        room_request_pending: false,
        room_request_action: "",
        socketError: message?.message || "Something went wrong.",
        lastSocketAction: action,
        powerUpPending: action === "usePowerUp" ? false : this.state.powerUpPending,
      });

      if (["leaveQueueUser", "leaveRoom"].includes(action) && this.pendingLeaveIntent === action) {
        this.pendingLeaveIntent = "";
      }
      return;
    }

    const commonState = {
      lastSocketAction: action,
      socketError: "",
    };

    switch (action) {
      case "createRoom":
      case "joinRoom": {
        this.clearRoomRequestTimer();
        const roomId = Number(payload?.battle_room_id || 0);
        const battleId = Number(payload?.battles_id || 0);
        const roomCode = String(payload?.room_code ?? "");
        if (!roomId || !battleId || !roomCode) {
          this.patch({ ...commonState, room_request_pending: false, room_request_action: "",
            socketError: "Room response is missing its code or battle details." });
          break;
        }
        const users = Array.isArray(payload?.users_list) ? payload.users_list : [];
        const currentMember = users.find((item) => Number(item?.user_id || 0) === this.userId);
        this.patch({
          ...commonState,
          ...payload,
          selectedBattle: { battles_id: battleId },
          selectedSubject: Number(payload?.subject_id || 0)
            ? { subject_id: Number(payload.subject_id), subject_name: payload?.creator_subject_name || this.state?.selectedSubject?.subject_name }
            : this.state?.selectedSubject,
          entryMode: "friend",
          liveStage: "private_room",
          isRoomCreator: Number(currentMember?.is_created || 0) === 1,
          battle_room_id: roomId,
          room_code: roomCode,
          users_list: users,
          game_entry_fee: Number(payload?.game_entry_fee ?? this.state?.game_entry_fee ?? 0),
          userCoins: Number(payload?.user_current_coins ?? this.state?.userCoins ?? 0),
          user_coins: Number(payload?.user_current_coins ?? this.state?.user_coins ?? 0),
          room_request_pending: false,
          room_request_action: "",
          roomNotice: message?.message || "Room is ready. Share the code with your friends.",
          opponent_room_left: false,
          friend_start_pending: roomId === Number(this.state?.battle_room_id || 0)
            ? Boolean(this.state?.friend_start_pending) : false,
        });
        break;
      }
      case "searchOpponent":
      case "matchPing": {
        if (this.state?.liveStage === "match_failed" || this.state?.liveStage === "left") break;
        const nextState = buildGamificationFlowState(
          {
            ...this.state,
            ...commonState,
            ...payload,
            entryMode: "random",
            liveStage: "queue",
            temp_room_id: Number(payload?.temp_room_id || this.state?.temp_room_id || 0),
            battle_room_id: Number(payload?.battle_room_id || this.state?.battle_room_id || 0),
            player_id: Number(payload?.player_id || this.state?.player_id || 0),
            userCoins: Number(
              payload?.user_current_coins ?? payload?.user_coins ?? this.state?.userCoins ?? 0
            ),
            user_coins: Number(
              payload?.user_current_coins ?? payload?.user_coins ?? this.state?.user_coins ?? 0
            ),
            users_list: payload?.users_list || [],
            queue_users: payload?.queue_users || [],
          },
          this.state
        );
        this.patch({
          ...nextState,
          start_confirmation_required: false,
          start_countdown_seconds: 0,
        });
        break;
      }
      case "startGame":
      case "startBattleGame": {
        if (action === "startGame" && this.state?.entryMode === "random" &&
            (!this.state?.start_confirmation_required || !this.state?.start_confirmed)) break;
        if (action === "startBattleGame" && this.state?.currentQuestion?.question_id) break;
        const nextState = buildGamificationFlowState(
          {
            ...this.state,
            ...commonState,
            ...payload,
            battle_room_id: Number(payload?.battle_room_id || this.state?.battle_room_id || 0),
            temp_room_id: Number(payload?.temp_room_id || this.state?.temp_room_id || 0),
            users_list: payload?.users_list || this.state?.users_list || [],
          },
          this.state
        );
        if (action === "startGame" && nextState?.entryMode === "random") {
          this.patch({
            ...nextState,
            liveStage: "queue",
            start_response_received: true,
            currentQuestion: null,
            question_id: 0,
            answerSaveResponse: null,
            answerSubmitted: false,
          });
          break;
        }

        this.patch({
          ...nextState,
          liveStage: "starting",
          start_confirmed: true,
          start_confirmation_required: false,
          start_countdown_seconds: 0,
          currentQuestion: null,
          question_id: 0,
          answerSaveResponse: null,
          answerSubmitted: false,
          power_up_state: null,
        });
        break;
      }
      case "question":
      case "rejoinGame": {
        if (action === "question" && this.state?.entryMode === "random" &&
            !["starting", "gameplay", "waiting_for_answer"].includes(this.state?.liveStage)) break;
        const currentQuestion = normalizeQuestionPayload(
          payload,
          this.state?.selected_lang || "EN"
        );
        if (!currentQuestion?.question_id) break;
        if (action === "question" && Number(this.state?.currentQuestion?.question_id) === currentQuestion.question_id) break;
        this.patch({
          ...commonState,
          ...payload,
          currentQuestion: {
            ...currentQuestion,
            timer_seconds: Number(action === "rejoinGame"
              ? payload?.remaining_time || 60
              : payload?.question_time || 60),
          },
          questionReceivedAt: Date.now(),
          battle_room_id: Number(payload?.battle_room_id || this.state?.battle_room_id || 0),
          question_id: Number(payload?.question_id || 0),
          question_index: Number(payload?.question_index || 0),
          total_questions: Number(payload?.total_questions || 0),
          remaining_coins: Number(payload?.remaining_coins || 0),
          liveStage: "gameplay",
          answerSaveResponse: null,
          power_up_state: null,
          answerSubmitted: false,
          powerUpPending: false,
        });
        break;
      }
      case "questionAnswerSave": {
        this.patch({
          ...commonState,
          answerSaveResponse: { ...payload, is_all_user_answered: payload?.isAllUserAnswered === true },
          liveStage: payload?.isAllUserAnswered === true
            ? "gameplay"
            : this.state?.answerSubmitted ? "waiting_for_answer" : this.state?.liveStage,
        });
        break;
      }
      case "battleScore": {
        this.patch({
          ...commonState,
          liveScore: payload,
        });
        break;
      }
      case "usePowerUp": {
        if (Number(payload?.question_id || 0) !== Number(this.state?.question_id || 0) ||
            Number(payload?.battle_room_id || 0) !== Number(this.state?.battle_room_id || 0)) break;
        const currentQuestion = this.state?.currentQuestion || null;
        const nextCurrentQuestion = currentQuestion
          ? normalizeQuestionPayload(
              {
                ...currentQuestion,
                ...payload,
                remaining_coins:
                  payload?.remaining_coins ??
                  payload?.user_current_coins ??
                  currentQuestion?.remaining_coins ??
                  this.state?.remaining_coins ??
                  0,
              },
              this.state?.selected_lang || "EN"
            )
          : currentQuestion;

        this.patch({
          ...commonState,
          power_up_state: payload,
          powerUpPending: false,
          currentQuestion: nextCurrentQuestion,
          remaining_coins: Number(
            payload?.remaining_coins ??
              payload?.user_current_coins ??
              this.state?.remaining_coins ??
              this.state?.userCoins ??
              0
          ),
        });
        break;
      }
      case "completeGame": {
        this.patch({
          ...commonState,
          liveStage: "completed",
          result_snapshot: payload,
          rank: Number(payload?.rank || this.state?.rank || 0),
          score: Number(payload?.coins || this.state?.score || 0),
          battleLeaderboardTarget: {
            battles_id: Number(this.state?.battles_id || 0),
            battle_room_id: Number(this.state?.battle_room_id || 0),
          },
        });
        break;
      }
      case "leaveRoom":
      case "leaveQueueUser": {
        const incomingRoomId = Number(payload?.room_id || payload?.battle_room_id || 0);
        if (action === "leaveRoom" && incomingRoomId && this.state?.battle_room_id &&
            incomingRoomId !== Number(this.state.battle_room_id)) break;
        if (this.pendingLeaveIntent === action) {
          this.pendingLeaveIntent = "";
          this.clearLiveIdentifiers();
          this.patch({ ...commonState, liveStage: "left" });
        } else {
          this.patch({
            ...commonState,
            users_list: Array.isArray(payload?.users_list) ? payload.users_list : this.state?.users_list,
            queue_users: Array.isArray(payload?.queue_users) ? payload.queue_users : this.state?.queue_users,
            opponent_room_left: action === "leaveRoom" && this.state?.entryMode === "friend",
            start_confirmation_required: Boolean(this.state?.start_confirmed && this.state?.start_confirmation_required),
            start_countdown_seconds: this.state?.start_confirmed ? this.state?.start_countdown_seconds : 0,
          });
        }
        break;
      }
      case "leaveGame": {
        this.patch({ ...commonState, leaveGameResponse: payload });
        break;
      }
      case "leaveUser": {
        this.patch({
          ...commonState,
          ...payload,
          users_list: Array.isArray(payload?.users_list) ? payload.users_list : this.state?.users_list,
          queue_users: Array.isArray(payload?.queue_users) ? payload.queue_users : this.state?.queue_users,
          start_confirmation_required: Boolean(this.state?.start_confirmed && this.state?.start_confirmation_required),
          start_countdown_seconds: this.state?.start_confirmed ? this.state?.start_countdown_seconds : 0,
          liveStage: this.state?.liveStage === "queue" ? "queue" : this.state?.liveStage,
        });
        break;
      }
      case "userRejoined": {
        this.patch({
          ...commonState,
          ...payload,
          users_list: Array.isArray(payload?.users_list) ? payload.users_list : this.state?.users_list,
        });
        break;
      }
      default: {
        this.patch({
          ...commonState,
          ...payload,
        });
      }
    }
  }

  sendAction(action, payload = {}) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN ||
        (action !== "pong" && !this.state.isSocketConnected)) {
      this.patch({
        socketError: "Socket is not connected.",
      });
      return false;
    }

    this.socket.send(
      JSON.stringify({
        action,
        ...payload,
      })
    );
    return true;
  }

  syncFlowState(nextState) {
    this.patch(nextState);
  }

  createRoom(payload = {}) {
    if (this.state?.room_request_pending) return false;
    const mergedState = buildGamificationFlowState(payload, this.state);
    if (!mergedState?.battles_id || !mergedState?.subject_id || !mergedState?.state_id) {
      this.patch({ socketError: "Select a battle, state, and subject first." });
      return false;
    }
    const sent = this.sendAction("createRoom", {
      battles_id: String(mergedState.battles_id),
      subject_id: String(mergedState.subject_id),
      states_id: String(mergedState.state_id),
    });
    if (sent) {
      this.patch({ entryMode: "friend", room_request_pending: true,
        room_request_action: "createRoom", socketError: "", roomNotice: "" });
      this.startRoomRequestTimer();
    }
    return sent;
  }

  joinRoom(payload = {}) {
    if (this.state?.room_request_pending) return false;
    const roomCode = String(payload?.room_code ?? "").trim();
    if (!roomCode) {
      this.patch({ socketError: "Enter a room code." });
      return false;
    }
    const withContext = payload?.includeContext !== false;
    const mergedState = buildGamificationFlowState(payload, this.state);
    const sent = this.sendAction("joinRoom", {
      room_code: roomCode,
      ...(withContext && Number(mergedState?.battles_id || 0) > 0 ? { battles_id: Number(mergedState.battles_id) } : {}),
      ...(withContext && Number(mergedState?.state_id || 0) > 0 ? { states_id: Number(mergedState.state_id) } : {}),
      ...(withContext && Number(mergedState?.subject_id || 0) > 0 ? { subject_id: Number(mergedState.subject_id) } : {}),
    });
    if (sent) {
      this.patch({ entryMode: "friend", room_request_pending: true,
        room_request_action: "joinRoom", socketError: "", roomNotice: "" });
      this.startRoomRequestTimer();
    }
    return sent;
  }

  searchOpponent(payload = {}) {
    const mergedState = buildGamificationFlowState(payload, this.state);
    this.patch({
      ...mergedState,
      entryMode: "random",
      liveStage: "queue",
      socketError: "",
    });
    return this.sendAction("searchOpponent", {
      battles_id: Number(mergedState?.battles_id || 0),
      states_id: Number(mergedState?.state_id || 0),
      subject_id: Number(mergedState?.subject_id || 0),
      temp_room_id: Number(mergedState?.temp_room_id || 0) || undefined,
    });
  }

  matchPing(payload = {}) {
    const mergedState = buildGamificationFlowState(payload, this.state);
    return this.sendAction("matchPing", {
      battles_id: Number(mergedState?.battles_id || 0),
      states_id: Number(mergedState?.state_id || 0),
      subject_id: Number(mergedState?.subject_id || 0),
      temp_room_id: Number(mergedState?.temp_room_id || 0),
    });
  }

  startGame(payload = {}) {
    const mergedState = buildGamificationFlowState(payload, this.state);
    return this.sendAction("startGame", {
      battles_id: String(mergedState?.battles_id || 0),
      states_id: String(mergedState?.state_id || 0),
      subject_id: String(mergedState?.subject_id || 0),
      temp_room_id: String(mergedState?.temp_room_id || 0),
      ...(Number(mergedState?.battle_room_id || 0) > 0 ? { battle_room_id: Number(mergedState.battle_room_id) } : {}),
    });
  }

  startBattleGame(payload = {}) {
    const mergedState = buildGamificationFlowState(payload, this.state);
    return this.sendAction("startBattleGame", {
      battles_id: String(mergedState?.battles_id || 0),
      battle_room_id: String(mergedState?.battle_room_id || 0),
      subject_id: String(mergedState?.subject_id || 0),
      states_id: String(mergedState?.state_id || 0),
      room_code: String(mergedState?.room_code || ""),
    });
  }

  requestQuestion(payload = {}) {
    const currentQuestion = this.state?.currentQuestion || {};
    return this.sendAction("question", {
      subject_id: String(this.state?.subject_id || 0),
      states_id: String(this.state?.state_id || 0),
      battles_id: String(this.state?.battles_id || 0),
      battle_room_id: String(this.state?.battle_room_id || 0),
      category_id: String(this.state?.category_id || 6),
      question_id: String(payload?.question_id ?? currentQuestion?.question_id ?? 0),
      question_index: String(payload?.question_index ?? currentQuestion?.question_index ?? 0),
      selected_lang: payload?.selected_lang || "EN",
    });
  }

  saveAnswer(payload = {}) {
    const answerPayload = {
      battle_room_id: String(this.state?.battle_room_id || 0),
      question_id: String(payload?.question_id || this.state?.question_id || 0),
      battles_id: String(this.state?.battles_id || 0),
      time_taken: String(payload?.time_taken || 0),
      user_answer_id: payload?.user_answer_id === "" ? "" : String(payload?.user_answer_id || 0),
    };

    if (Number(payload?.is_guess || 0) === 1) {
      answerPayload.is_guess = 1;
    }

    const sent = this.sendAction("questionAnswerSave", answerPayload);
    if (sent) this.patch({ answerSubmitted: true });
    return sent;
  }

  usePowerUp(power_up_key) {
    if (this.state.powerUpPending || this.state.answerSubmitted) return false;
    const sent = this.sendAction("usePowerUp", {
      battle_room_id: String(this.state?.battle_room_id || 0),
      battles_id: String(this.state?.battles_id || 0),
      question_id: String(this.state?.question_id || 0),
      power_up_key,
    });
    if (sent) this.patch({ powerUpPending: true });
    return sent;
  }

  battleScore() {
    return this.sendAction("battleScore", {
      battle_room_id: String(this.state?.battle_room_id || 0),
      battles_id: String(this.state?.battles_id || 0),
    });
  }

  completeGame() {
    return this.sendAction("completeGame", {
      battle_room_id: String(this.state?.battle_room_id || 0),
      battles_id: String(this.state?.battles_id || 0),
    });
  }

  leaveRoom() {
    const roomId = Number(this.state?.battle_room_id || 0);
    const roomCode = this.state?.room_code || "";
    const sent = this.sendAction("leaveRoom", {
      room_id: String(roomId),
      battles_id: String(this.state?.battles_id || 0),
      room_code: String(roomCode),
    });
    if (sent) {
      this.pendingLeaveIntent = "leaveRoom";
      this.clearLiveIdentifiers();
    }
    return sent;
  }

  leaveQueueUser() {
    const playerId = Number(this.state?.player_id || 0);
    const sent = this.sendAction("leaveQueueUser", {
      player_id: playerId,
    });
    if (sent) {
      this.pendingLeaveIntent = "leaveQueueUser";
      this.clearLiveIdentifiers();
    }
    return sent;
  }

  leaveGame() {
    const sent = this.sendAction("leaveGame", {
      room_id: String(this.state?.battle_room_id || 0),
      battles_id: String(this.state?.battles_id || 0),
      question_id: String(this.state?.question_id || 0),
    });
    if (sent) {
      this.clearLiveIdentifiers();
    }
    return sent;
  }

  clearLiveIdentifiers() {
    this.clearRoomRequestTimer();
    this.patch({
      battle_room_id: 0,
      temp_room_id: 0,
      room_code: "",
      room_request_pending: false,
      room_request_action: "",
      roomNotice: "",
      opponent_room_left: false,
      friend_start_pending: false,
      isRoomCreator: false,
      player_id: 0,
      users_list: [],
      queue_users: [],
      currentQuestion: null,
      question_id: 0,
      question_index: 0,
      total_questions: 0,
      power_up_state: null,
      liveScore: null,
      start_confirmation_required: false,
      start_countdown_seconds: 0,
      confirmation_started_at: 0,
      start_confirmed: false,
      start_response_received: false,
      matchFailureMessage: "",
      liveStage: "",
      answerSaveResponse: null,
    });
  }

  clearSession() {
    clearStoredGamificationFlowState();
    this.replace({});
  }
}

let sharedClient = null;

export function getGamificationBattleClient() {
  if (!sharedClient) {
    sharedClient = new GamificationBattleClient();
  }

  return sharedClient;
}




